import { fmtDate, invNo, num, periodLabel, shortPeriod } from "@/lib/format";

export const DEFAULT_DESIGN = {
  accent_color: "#0f172a", header_style: "line", border_style: "dashed", title: "NOTA",
  show_logo: true, show_company_address: true, show_company_phone: true, show_period: true,
  show_customer_address: true, show_customer_phone: false, show_price: true, show_footer: true,
  show_signature: true, signature_label: "Hormat kami,", signer_name: "", show_stamp: false, paid_label: "",
  signature_img: null, stamp_img: null, show_prev_debt: true,
};

const tint = (hex) => `${hex}1a`;

function NotaHeader({ inv, tenant, d, fs }) {
  const solid = d.header_style === "solid";
  return (
    <div className="flex items-start gap-[2mm] pb-[1.2mm] px-[1.5mm] pt-[1mm]"
      style={solid ? { background: d.accent_color, color: "#fff", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" } : { borderBottom: `2px solid ${d.accent_color}` }}>
      {d.show_logo && tenant.logo && <img src={tenant.logo} alt="" style={{ height: `${fs * 3.6}pt`, width: `${fs * 3.6}pt`, background: solid ? "#fff" : "transparent" }} className="object-contain rounded-sm" />}
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold uppercase" style={{ fontSize: `${fs * 1.35}pt`, color: solid ? "#fff" : d.accent_color }}>{tenant.name}</div>
        {d.show_company_address && <div className="truncate">{tenant.address}</div>}
        {d.show_company_phone && <div className="truncate">{tenant.phone}</div>}
      </div>
      <div className="text-right">
        <div className="font-display font-bold tracking-wider" style={{ fontSize: `${fs * 1.2}pt`, color: solid ? "#fff" : d.accent_color }}>{d.title || "NOTA"}</div>
        <div className="font-mono font-semibold">{invNo(inv)}</div>
      </div>
    </div>
  );
}

function NotaFooter({ tenant, d, fs }) {
  return (
    <div className="mt-auto flex items-end justify-between pt-[1mm]">
      <div className="max-w-[58%]">
        {d.paid_label && <div className="inline-block font-display font-bold tracking-widest border-2 px-[2mm] -rotate-6 mb-[1mm]" style={{ color: d.accent_color, borderColor: d.accent_color, fontSize: `${fs * 1.2}pt` }}>{d.paid_label}</div>}
        {d.show_footer && <div className="italic text-slate-600 truncate">{tenant.nota_footer}</div>}
      </div>
      {d.show_signature && (
        <div className="text-center relative">
          <div>{d.signature_label}</div>
          <div className="relative flex items-center justify-center" style={{ height: `${fs * 2.6}pt` }}>
            {d.signature_img && <img src={d.signature_img} alt="" className="max-h-full object-contain relative z-[1]" />}
            {d.show_stamp && d.stamp_img && <img src={d.stamp_img} alt="" className="absolute object-contain opacity-80 -rotate-12" style={{ height: `${fs * 4.2}pt`, left: "-30%" }} />}
          </div>
          <div className="border-t border-slate-700 px-[4mm]">{d.signer_name || tenant.name}</div>
        </div>
      )}
    </div>
  );
}

export function NotaCard({ inv, tenant, rows }) {
  const d = { ...DEFAULT_DESIGN, ...(tenant.nota_design || {}) };
  const adds = inv.additions || [];
  const hasAdd = adds.length > 0;
  const fs = (rows >= 6 ? 6.5 : rows >= 5 ? 7.2 : rows >= 4 ? 8.2 : 9.5) * (hasAdd && rows >= 4 ? 0.82 : 1);
  const th = { background: tint(d.accent_color), color: d.accent_color, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" };
  const R = { textAlign: "right" };
  const addTotal = adds.reduce((s, a) => s + a.total, 0);
  const span = (d.show_price ? 3 : 2) + (hasAdd ? 2 : 0);
  return (
    <div className="nota relative" style={{ fontSize: `${fs}pt`, lineHeight: 1.25, borderStyle: d.border_style === "none" ? "none" : d.border_style }} data-testid={`nota-${inv.number}`}>
      <NotaHeader inv={inv} tenant={tenant} d={d} fs={fs} />
      <div className="grid grid-cols-[auto_1fr] gap-x-[2mm] py-[1mm]">
        <span>Tanggal</span><span>: {fmtDate(inv.date)}{d.show_period ? ` · Periode ${periodLabel(inv.period)}` : ""}</span>
        <span>Pelanggan</span><span className="font-semibold truncate">: {inv.customer_name}</span>
        {d.show_customer_address && <><span>Alamat</span><span className="truncate">: {inv.customer_address || "-"}</span></>}
        {d.show_customer_phone && <><span>Telepon</span><span className="truncate">: {inv.customer_phone || "-"}</span></>}
      </div>
      <table>
        <thead><tr><th style={th}>Jenis Voucher</th>{hasAdd && <><th style={{ ...th, ...R }}>Awal</th><th style={{ ...th, ...R }}>Tambah</th></>}<th style={{ ...th, ...R }}>Jumlah</th>{d.show_price && <th style={{ ...th, ...R }}>Harga</th>}<th style={{ ...th, ...R }}>Total</th></tr></thead>
        <tbody>
          {inv.lines.map((l) => (
            <tr key={l.voucher_type_id}>
              <td>{l.voucher_name}</td>
              {hasAdd && <><td className="font-mono" style={R}>{num(l.qty - (l.add_qty || 0))}</td><td className="font-mono" style={R}>{l.add_qty ? `+${num(l.add_qty)}` : "-"}</td></>}
              <td className="font-mono" style={R}>{num(l.qty)}</td>
              {d.show_price && <td className="font-mono" style={R}>{num(l.price)}</td>}
              <td className="font-mono" style={R}>{num(l.total)}</td>
            </tr>
          ))}
          {hasAdd && (
            <>
              <tr data-testid={`nota-base-total-${inv.number}`}>
                <td colSpan={span} style={R}>Tagihan pasokan awal</td>
                <td className="font-mono" style={R}>{num(inv.total - addTotal)}</td>
              </tr>
              {adds.map((a) => (
                <tr key={a.rev} data-testid={`nota-addition-${inv.number}-${a.rev}`}>
                  <td colSpan={span} style={R} className="whitespace-nowrap">Tambahan voucher {fmtDate(a.date)}</td>
                  <td className="font-mono" style={R}>{num(a.total)}</td>
                </tr>
              ))}
            </>
          )}
          <tr>
            <td colSpan={span} style={R} className="font-bold">TOTAL (Rp)</td>
            <td className="font-mono font-bold" style={{ ...R, color: d.accent_color }}>{num(inv.total)}</td>
          </tr>
          {d.show_prev_debt && inv.prev_debt?.total > 0 && (
            <tr data-testid={`nota-prev-debt-${inv.number}`}>
              <td colSpan={span} style={{ ...R, color: "#b91c1c" }} className="font-semibold">Sisa hutang sebelumnya ({inv.prev_debt.periods.map((x) => shortPeriod(x.period)).join(", ")})</td>
              <td className="font-mono font-bold" style={{ ...R, color: "#b91c1c" }}>{num(inv.prev_debt.total)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <NotaFooter tenant={tenant} d={d} fs={fs} />
    </div>
  );
}

export function NotaPages({ invoices, tenant, perPage }) {
  const cols = perPage >= 4 ? 2 : 1;
  const rows = Math.ceil(perPage / cols);
  const pages = [];
  for (let i = 0; i < invoices.length; i += perPage) pages.push(invoices.slice(i, i + perPage));
  return (
    <div className="preview-zoom flex flex-col items-center gap-6" data-testid="nota-pages">
      {pages.map((pg, pi) => (
        <div key={pi} className="a4-page shadow-lg" data-testid={`a4-page-${pi + 1}`}
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}>
          {pg.map((inv) => <NotaCard key={inv.id} inv={inv} tenant={tenant} rows={rows} />)}
        </div>
      ))}
    </div>
  );
}
